import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  closestCenter,
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { PlusIcon } from 'lucide-react';
import { useAssetApprovalsHidden } from '@/shared/hooks/useAssetApprovalsHidden';
import type {
  CustomColumn,
  CustomColumnType,
  ScrollContainersRef,
  SyncedScrollLeftRef,
} from '../../lib/useTrackersBoard';
import { APPROVAL_COLUMN_TYPES } from '../../lib/column-catalog';
import { TrackersProjectGroup, type ColumnMenuActions } from '../TrackersProjectGroup';
import { TrackersBoardSkeleton } from '../TrackersBoardSkeleton';
import { type ColumnSort } from '../../lib/sorting';
import type { BoardRowItem } from '../TrackersBriefRow';
import { useCanEditTracker } from '../../lib/tracker-access';
import { useTranslation } from 'react-i18next';

/** A rendered group: a header + its rows. Source-agnostic (briefs or items). */
export interface BoardGroup {
  id: string;
  title: string | null;
  /** Accent color override (custom groups); defaults to the palette by index. */
  color?: string;
  items: BoardRowItem[];
  itemCount: number;
  /** Rows before search/filters narrowed them; drives the empty-group copy. */
  unfilteredItemCount: number;
}

interface TrackersBoardTableProps {
  groups: BoardGroup[];
  groupIds: string[];
  isLoading: boolean;
  expandedGroupIds: Set<string>;
  onToggleGroup: (groupId: string) => void;
  onGroupDragStart: () => void;
  onGroupDragEnd: (activeId: string, overId: string) => void;
  onReorderItems: (groupId: string, activeId: string, overId: string) => void;
  /**
   * Move an item to another group at a position. Providing this switches item
   * DnD to a single table-level context so rows can be dragged BETWEEN groups
   * (custom trackers). `beforeItemId` = the item to drop in front of, or null
   * to append. When absent (briefs), items reorder within their group only.
   */
  onMoveItem?: (itemId: string, toGroupId: string, beforeItemId: string | null) => void;
  /** Append a new (custom) group. Renders the "+ Add group" button when set. */
  onAddGroup?: () => void;
  /** Inline-rename a (custom) group. */
  onRenameGroup?: (groupId: string, nextLabel: string) => void;
  /** Delete a (custom) group. */
  onDeleteGroup?: (groupId: string) => void;
  columnOrder: string[];
  columnWidths: Record<string, number>;
  onReorderColumns: (activeId: string, overId: string) => void;
  onSetColumnWidth: (columnId: string, width: number) => void;
  itemColumnWidth: number;
  onSetItemColumnWidth: (width: number) => void;
  scrollContainersRef: ScrollContainersRef;
  syncedScrollLeftRef: SyncedScrollLeftRef;
  /**
   * The page's vertical scroll container (the shared list body). Rows virtualize
   * against it so the whole board scrolls with one scrollbar instead of each
   * group owning a nested inner scroller.
   */
  boardScrollRef?: RefObject<HTMLDivElement | null>;
  tableMinContentWidth: number;
  onAddCustomColumn: (params: { type: CustomColumnType }) => void;
  onAddCustomColumnAfter: (afterColumnId: string, params: { type: CustomColumnType }) => void;
  onDuplicateColumn: (columnId: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onConfigureColumn: (columnId: string) => void;
  /** Active column sort (drives header arrows) + toggle, when sorting is on. */
  columnSort?: ColumnSort | null;
  onToggleColumnSort?: (columnId: string) => void;
  columnDefs: Record<string, { label: string; minWidth: number; type?: CustomColumnType }>;
  customColumns: CustomColumn[];
  onRenameColumn: (columnId: string, nextLabel: string) => void;
  /** Column types hidden from the Add-column picker (e.g. custom trackers
   *  exclude the brief-bound `brief_type` / `assets` types). */
  excludeColumnTypes?: readonly CustomColumnType[];
  // ── Row behavior (briefs default; free-form items opt in) ──
  onItemClick?: (id: string) => void;
  /** Preview the row's file from its thumbnail cell — see `TrackersBriefRow`. */
  onOpenPreview?: (id: string) => void;
  /** The tracker these rows belong to — keys each row's comment thread. */
  trackerId?: string;
  onOpenComments?: (id: string) => void;
  /** Row whose comments panel is open — see `TrackersBriefRow`. */
  openCommentsRowKey?: string | null;
  showComments?: boolean;
  editableTitle?: boolean;
  onRenameItem?: (id: string, next: string) => void;
  onSetCellValue?: (itemId: string, columnId: string, value: unknown) => void;
  /** Which columns accept an edit — see `TrackersBriefRow`. */
  isColumnEditable?: (columnId: string, itemId: string) => boolean;
  /** Which columns the board can order by — see `TrackersProjectGroup`. */
  isColumnSortable?: (columnId: string) => boolean;
  /** Which kebab actions each column honours — see `TrackersProjectGroup`. */
  columnMenu?: (columnId: string) => ColumnMenuActions;
  /** Rows and groups can be dragged into a new order — see `TrackersBriefRow`. */
  reorderable?: boolean;
  onDeleteItem?: (id: string) => void;
  /** Inline add-item row per group (free-form items). */
  onAddItem?: (groupId: string, title: string) => void;
}

export function TrackersBoardTable(props: TrackersBoardTableProps) {
  const { t } = useTranslation('trackers');

  const {
    groups,
    groupIds,
    isLoading,
    expandedGroupIds,
    onToggleGroup,
    onGroupDragStart,
    onGroupDragEnd,
    onReorderItems,
    onMoveItem: onMoveItemProp,
    onAddGroup: onAddGroupProp,
    onRenameGroup: onRenameGroupProp,
    onDeleteGroup: onDeleteGroupProp,
    columnOrder: allColumnOrder,
    columnWidths,
    onReorderColumns,
    onSetColumnWidth,
    itemColumnWidth,
    onSetItemColumnWidth,
    scrollContainersRef,
    syncedScrollLeftRef,
    boardScrollRef,
    tableMinContentWidth: fullMinContentWidth,
    onAddCustomColumn,
    onAddCustomColumnAfter,
    onDuplicateColumn,
    onDeleteColumn,
    onConfigureColumn,
    columnSort,
    onToggleColumnSort,
    columnDefs,
    customColumns,
    onRenameColumn,
    excludeColumnTypes,
    onItemClick,
    onOpenPreview,
    trackerId,
    onOpenComments,
    openCommentsRowKey = null,
    showComments,
    editableTitle,
    onRenameItem,
    onSetCellValue,
    isColumnEditable,
    isColumnSortable,
    columnMenu,
    reorderable: reorderableProp = true,
    onDeleteItem,
    onAddItem: onAddItemProp,
  } = props;
  const canEdit = useCanEditTracker();
  const reorderable = canEdit && reorderableProp;
  const onMoveItem = canEdit ? onMoveItemProp : undefined;
  const onAddGroup = canEdit ? onAddGroupProp : undefined;
  const onRenameGroup = canEdit ? onRenameGroupProp : undefined;
  const onDeleteGroup = canEdit ? onDeleteGroupProp : undefined;
  const onAddItem = canEdit ? onAddItemProp : undefined;

  // An approval column IS the approvals surface in board form, so
  // `hide_asset_approvals` drops the whole column — header included — rather
  // than leaving one titled "Approval" with an empty cell under every row.
  // Only the render list is filtered: the persisted layout still carries the
  // column, so turning the flag back off restores it in place.
  const assetApprovalsHidden = useAssetApprovalsHidden();
  const hiddenColumnIds = useMemo(
    () =>
      assetApprovalsHidden
        ? allColumnOrder.filter((id) => {
            // columnDefs is a sparse map keyed by dynamic column ids; eslint
            // reads it as total (noUncheckedIndexedAccess is off) but a
            // stale/absent id genuinely resolves to undefined at runtime.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const type = columnDefs[id]?.type;
            return type !== undefined && APPROVAL_COLUMN_TYPES.includes(type);
          })
        : [],
    [assetApprovalsHidden, allColumnOrder, columnDefs],
  );
  const columnOrder = useMemo(
    () =>
      hiddenColumnIds.length
        ? allColumnOrder.filter((id) => !hiddenColumnIds.includes(id))
        : allColumnOrder,
    [allColumnOrder, hiddenColumnIds],
  );
  const tableMinContentWidth = useMemo(
    () =>
      hiddenColumnIds.reduce((width, id) => {
        // Same fallback chain each board used to add the column up: an explicit
        // width if the user ever resized it, the type minimum otherwise. Sparse
        // maps again, hence the disable.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const columnWidth = columnWidths[id] ?? columnDefs[id]?.minWidth ?? 0;
        return width - columnWidth;
      }, fullMinContentWidth),
    [hiddenColumnIds, columnWidths, columnDefs, fullMinContentWidth],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // When `onMoveItem` is wired, item rows share this one table-level context
  // (see TrackersProjectGroup#unifiedItemDnd) so they can be dragged between
  // groups. Otherwise the table-level context handles group reorder only and
  // items keep their per-group contexts.
  const unifiedItems = Boolean(onMoveItem);

  // itemId → its current group; itemId → the row (for the drag overlay).
  const itemToGroup = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) for (const it of g.items) map.set(it.id, g.id);
    return map;
  }, [groups]);
  const itemById = useMemo(() => {
    const map = new Map<string, BoardRowItem>();
    for (const g of groups) for (const it of g.items) map.set(it.id, it);
    return map;
  }, [groups]);

  const [activeItem, setActiveItem] = useState<BoardRowItem | null>(null);

  // Signals a layout shift that can move a group's virtualization offset: any
  // group expanding/collapsing or changing row count. Groups re-measure their
  // `scrollMargin` when this changes (see TrackersProjectGroup) — avoids a live
  // content ResizeObserver that thrashed during scroll.
  const layoutVersion = useMemo(
    () =>
      groups.map((g) => `${g.id}:${g.itemCount}:${expandedGroupIds.has(g.id) ? 1 : 0}`).join('|'),
    [groups, expandedGroupIds],
  );

  /** Resolve a drop target (group id, item id, or group card) to a group id. */
  const resolveGroup = (overId: string): string | undefined =>
    groupIds.includes(overId) ? overId : itemToGroup.get(overId);

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (groupIds.includes(activeId)) {
      onGroupDragStart();
      return;
    }
    if (unifiedItems) setActiveItem(itemById.get(activeId) ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveItem(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    // ── Group reorder ──
    if (groupIds.includes(activeId)) {
      const overGroup = resolveGroup(overId);
      if (overGroup && overGroup !== activeId) onGroupDragEnd(activeId, overGroup);
      return;
    }

    // ── Item drag (only when unified) ──
    if (!unifiedItems) return;
    const fromGroup = itemToGroup.get(activeId);
    const toGroup = resolveGroup(overId);
    if (!fromGroup || !toGroup) return;
    if (toGroup === fromGroup) {
      // Reorder within the group — `over` must be a sibling item, not the
      // group card itself (which would be a no-op move to the same group).
      if (!groupIds.includes(overId) && overId !== activeId) {
        onReorderItems(fromGroup, activeId, overId);
      }
      return;
    }
    // Cross-group move: insert before the hovered item, or append when the
    // drop landed on the group card / empty area.
    const beforeItemId = groupIds.includes(overId) ? null : overId;
    onMoveItem?.(activeId, toGroup, beforeItemId);
  };

  // Single horizontal scrollbar pinned to the bottom of the board.
  const syntheticScrollRef = useRef<HTMLDivElement | null>(null);
  // Wraps the whole board (groups + bottom bar) so one MutationObserver can
  // watch every scroll container mount/unmount.
  const boardRef = useRef<HTMLDivElement | null>(null);

  // Measured max content width across group tables (drives the bar's inner
  // width) and whether any group actually overflows (drives bar visibility).
  const [contentWidth, setContentWidth] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Scan every group's scroll wrapper for the widest content + whether any
  // overflows. Hoisted (not effect-local) so a column-resize can re-run it
  // directly — a ResizeObserver on the wrapper never fires for that case, since
  // resizing a column grows the inner table's `scrollWidth` while the wrapper's
  // own box stays the same size.
  const measure = useCallback(() => {
    const root = boardRef.current;
    if (!root) return;
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- string overload; rule flags the symbol for an unrelated deprecated tag-name overload
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-trackers-hscroll="group"]'));
    let max = 0;
    let overflow = false;
    for (const el of nodes) {
      if (el.scrollWidth > max) max = el.scrollWidth;
      if (el.scrollWidth > el.clientWidth + 1) overflow = true;
    }
    if (max > 0) setContentWidth(max);
    setHasOverflow(overflow);
  }, []);

  // Column/item resize changes table width but not the wrapper's box, so the
  // ResizeObserver below won't fire — re-measure on the next frame (after the
  // new width lays out) whenever the computed min content width changes.
  useEffect(() => {
    const id = requestAnimationFrame(() => measure());
    return () => cancelAnimationFrame(id);
  }, [measure, tableMinContentWidth]);

  // Horizontal-scroll sync + overflow measurement for the whole board.
  //
  // Every group's scroll wrapper is tagged `data-trackers-hscroll="group"` (the
  // bottom bar is `="bar"`) — but those wrappers MOUNT/UNMOUNT as groups are
  // expanded/collapsed, grouping changes, or rows are added/removed. A fixed
  // dependency list can't see those DOM changes, so the listener set went stale
  // (only the group you scrolled moved; the bar vanished) until a refresh.
  // Instead a MutationObserver re-wires whenever scroll containers appear or
  // disappear, and a ResizeObserver re-measures on width changes — so it can
  // never drift out of sync with the actual DOM.
  useEffect(() => {
    const root = boardRef.current;
    if (!root) return;

    const groupNodes = () =>
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- string overload; rule flags the symbol for an unrelated deprecated tag-name overload
      Array.from(root.querySelectorAll<HTMLElement>('[data-trackers-hscroll="group"]'));
    const allNodes = () =>
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- string overload; rule flags the symbol for an unrelated deprecated tag-name overload
      Array.from(root.querySelectorAll<HTMLElement>('[data-trackers-hscroll]'));

    const ro = new ResizeObserver(() => measure());

    let detachers: Array<() => void> = [];
    let syncing = false;
    const rewire = () => {
      detachers.forEach((d) => d());
      const nodes = allNodes();
      // Align freshly-mounted containers to the shared scroll position.
      for (const n of nodes) {
        n.scrollLeft = syncedScrollLeftRef.current;
        n.dataset.scrolled = String(syncedScrollLeftRef.current > 0);
      }
      detachers = nodes.map((node) => {
        const onScroll = () => {
          if (syncing) return;
          const left = node.scrollLeft;
          if (left === syncedScrollLeftRef.current) return;
          syncing = true;
          syncedScrollLeftRef.current = left;
          for (const other of nodes) {
            if (other !== node && other.scrollLeft !== left) other.scrollLeft = left;
            other.dataset.scrolled = String(left > 0);
          }
          syncing = false;
        };
        node.addEventListener('scroll', onScroll, { passive: true });
        return () => node.removeEventListener('scroll', onScroll);
      });
      // (Re)observe the group containers for width changes.
      ro.disconnect();
      for (const el of groupNodes()) ro.observe(el);
    };

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        rewire();
        measure();
      });
    };

    schedule();
    // Re-wire only when a scroll container (group card / bottom bar) is actually
    // added or removed — not on every cell keystroke.
    const mo = new MutationObserver((records) => {
      const structural = records.some((r) =>
        [...r.addedNodes, ...r.removedNodes].some((n) => {
          if (!(n instanceof HTMLElement)) return false;
          if (n.matches('[data-trackers-hscroll]')) return true;
          // eslint-disable-next-line @typescript-eslint/no-deprecated -- string overload; rule flags the symbol for an unrelated deprecated tag-name overload
          return n.querySelector('[data-trackers-hscroll]') !== null;
        }),
      );
      if (structural) schedule();
    });
    mo.observe(root, { childList: true, subtree: true });
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
      ro.disconnect();
      detachers.forEach((d) => d());
      window.removeEventListener('resize', measure);
    };
    // `isLoading`/`groups.length` re-run this once the board actually renders
    // (the loading + empty states early-return before `boardRef` mounts, so the
    // effect must re-attach when real content appears). All subsequent DOM
    // changes are handled by the observers, not the dep list.
  }, [syncedScrollLeftRef, isLoading, groups.length, measure]);

  if (isLoading) {
    return <TrackersBoardSkeleton />;
  }

  if (groups.length === 0) {
    return (
      <div className="flex h-20 items-center justify-center text-sm text-muted-foreground">
        {t('trackersBoard.trackersBoardTable.noBriefsFound')}
      </div>
    );
  }

  return (
    <div ref={boardRef} className="contents">
      <DndContext
        sensors={sensors}
        collisionDetection={unifiedItems ? closestCorners : closestCenter}
        modifiers={[restrictToVerticalAxis]}
        autoScroll={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-6 pb-3">
            {groups.map((group, index) => (
              <TrackersProjectGroup
                key={group.id}
                boardScrollRef={boardScrollRef}
                layoutVersion={layoutVersion}
                project={{ id: group.id, title: group.title }}
                color={group.color}
                items={group.items}
                itemCount={group.itemCount}
                unfilteredItemCount={group.unfilteredItemCount}
                isExpanded={expandedGroupIds.has(group.id)}
                onToggle={() => onToggleGroup(group.id)}
                colorIndex={index}
                onReorderItems={onReorderItems}
                unifiedItemDnd={unifiedItems}
                onRenameGroup={onRenameGroup}
                onDeleteGroup={onDeleteGroup}
                columnOrder={columnOrder}
                columnWidths={columnWidths}
                onReorderColumns={onReorderColumns}
                onSetColumnWidth={onSetColumnWidth}
                itemColumnWidth={itemColumnWidth}
                onSetItemColumnWidth={onSetItemColumnWidth}
                scrollContainersRef={scrollContainersRef}
                syncedScrollLeftRef={syncedScrollLeftRef}
                tableMinContentWidth={tableMinContentWidth}
                onAddCustomColumn={onAddCustomColumn}
                onAddCustomColumnAfter={onAddCustomColumnAfter}
                onDuplicateColumn={onDuplicateColumn}
                onDeleteColumn={onDeleteColumn}
                onConfigureColumn={onConfigureColumn}
                columnSort={columnSort}
                onToggleColumnSort={onToggleColumnSort}
                columnDefs={columnDefs}
                customColumns={customColumns}
                onRenameColumn={onRenameColumn}
                excludeColumnTypes={excludeColumnTypes}
                onItemClick={onItemClick}
                onOpenPreview={onOpenPreview}
                trackerId={trackerId}
                onOpenComments={onOpenComments}
                openCommentsRowKey={openCommentsRowKey}
                showComments={showComments}
                editableTitle={editableTitle}
                onRenameItem={onRenameItem}
                onSetCellValue={onSetCellValue}
                isColumnEditable={isColumnEditable}
                isColumnSortable={isColumnSortable}
                columnMenu={columnMenu}
                reorderable={reorderable}
                onDeleteItem={onDeleteItem}
                onAddItem={onAddItem ? (title) => onAddItem(group.id, title) : undefined}
              />
            ))}
          </div>
        </SortableContext>
        {/* Floating representation of the row being dragged between groups.
           dropAnimation={null}: the optimistic mutation has already re-bucketed
           the item, so the default glide-back reads as a snap-back. */}
        <DragOverlay dropAnimation={null}>
          {activeItem ? (
            <div
              style={{ width: itemColumnWidth }}
              className="truncate rounded-md border border-border bg-background px-3 py-2.5 text-sm font-medium text-foreground shadow-lg"
            >
              {activeItem.title}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {onAddGroup ? (
        <div className="pb-3">
          <button
            type="button"
            onClick={onAddGroup}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <PlusIcon className="size-4" aria-hidden />
            {t('trackersBoard.trackersBoardTable.addGroup')}
          </button>
        </div>
      ) : null}

      {/* Single horizontal scrollbar for the whole board — pinned to the bottom
         of the scroll viewport and mirrored to every group via the shared
         data-trackers-hscroll sync (native scroll listener). Opaque background
         so rows don't show through; NO top border — a hard border here reads as
         a stray divider after the last group (the group cards already have their
         own borders). Rendered ONLY when a group actually overflows. */}
      {hasOverflow ? (
        <div
          ref={syntheticScrollRef}
          data-trackers-hscroll="bar"
          className="sticky bottom-0 z-20 overflow-x-auto bg-card"
          aria-hidden="true"
        >
          <div style={{ width: `${Math.max(contentWidth, tableMinContentWidth)}px`, height: 12 }} />
        </div>
      ) : null}
    </div>
  );
}
